// Núcleo PURO (sin store ni axios) del "lease" de mesa y del último cobro
// compartido — testeable.
//
// Fase 1 del proyecto TPV (2026-09-29, rama feature/tpv-realtime-performance).
// Regla del usuario: solo un camarero/dispositivo dentro de una mesa a la
// vez; manda el ÚLTIMO en entrar (takeover). Si el que la tiene está
// COBRANDO, quitársela pide confirmación. Todos ven quién está en cada mesa.
//
// Contrato backend:
//   POST   mesas/{idMesa}/lease        { mode: "edit"|"charge", force }
//   POST   mesas/lease/heartbeat       { leaseIds } → { alive, lost }
//   DELETE mesas/lease/{leaseId}
//   GET    mesas/leases                → [ { leaseId, mesaId, mode, expiresAt, holder } ]
//   Hub mesahub `mesa/lease/{rid}`     { action, mesaId, leaseId, mode, holder, previousHolder?, ts }
//   Hub mesahub `payment/{rid}`        { paymentHistoryId, idMesa, mesaName, … }
//   Header `X-Mesa-Lease` en mutaciones; 409 LEASE_HELD / LEASE_CHARGING.

export const LEASE_TTL_MS = 30_000;
export const HEARTBEAT_MS = 10_000;
// Al desmontar un layout (cambio TPV grande ⇄ carta camarero) el nuevo
// vuelve a pedir la misma mesa: esperamos un poco antes de soltarla para
// no emitir "released"+"acquired" a todos los dispositivos en cada swap.
export const RELEASE_GRACE_MS = 2_000;

export const LEASE_MODE_EDIT = "edit";
export const LEASE_MODE_CHARGE = "charge";
export const LEASE_HEADER = "X-Mesa-Lease";

const MUTATING = new Set(["post", "put", "patch", "delete"]);

// ── Identidad ────────────────────────────────────────────────────────

const deviceIdOf = (holderOrId) =>
  holderOrId && typeof holderOrId === "object" ? holderOrId.deviceId : holderOrId;

/** ¿Este holder (o deviceId) es este dispositivo? */
export function isMe(holderOrId, myDeviceId) {
  const id = deviceIdOf(holderOrId);
  if (!id || !myDeviceId) return false;
  return String(id).toLowerCase() === String(myDeviceId).toLowerCase();
}

/** Iniciales para el badge: "Ana García" → "AG"; sin nombre → etiqueta del device. */
export function holderInitials(holder) {
  const name = (holder?.employeeName || holder?.deviceLabel || "").trim();
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** "Ana (TPV 2)" / "Ana" / "TPV 2" / "otro dispositivo". */
export function holderLabel(holder) {
  const name = holder?.employeeName?.trim();
  const device = holder?.deviceLabel?.trim();
  if (name && device) return `${name} (${device})`;
  return name || device || "otro dispositivo";
}

// ── Presencia (byMesaId) ─────────────────────────────────────────────

const toMs = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};

export function isLeaseExpired(lease, now = Date.now()) {
  const exp = toMs(lease?.expiresAt);
  return exp != null && exp <= now;
}

const normalizeLease = (l) => ({
  leaseId: l.leaseId,
  mesaId: l.mesaId,
  mode: l.mode || LEASE_MODE_EDIT,
  expiresAt: l.expiresAt ?? null,
  holder: l.holder || null,
});

/** Snapshot del GET: reemplaza la presencia entera (ignora caducados). */
export function presenceFromSnapshot(list, now = Date.now()) {
  const byMesaId = {};
  (Array.isArray(list) ? list : []).forEach((l) => {
    if (!l?.mesaId || isLeaseExpired(l, now)) return;
    byMesaId[l.mesaId] = normalizeLease(l);
  });
  return byMesaId;
}

/**
 * Aplica un evento `mesa/lease/{rid}` a la presencia.
 * Devuelve `{ byMesaId, takeover, reacquire }`; `takeover` = { mesaId, holder } cuando
 * el evento significa que ESTE dispositivo ha perdido una mesa que tenía
 * (revoked con previousHolder = yo, o acquired por otro sobre una mesa mía).
 * `reacquire` = mesaId cuando MI lease caducó sin que nadie la tomara.
 * No muta la entrada.
 */
export function applyLeaseEvent(byMesaId, myLeases, evt, myDeviceId) {
  const next = { ...(byMesaId || {}) };
  if (!evt?.mesaId) return { byMesaId: next, takeover: null, reacquire: null };
  const { action, mesaId } = evt;
  const mine = myLeases?.[mesaId];
  let takeover = null;
  let reacquire = null;

  switch (action) {
    case "acquired":
    case "mode":
      next[mesaId] = normalizeLease(evt);
      if (mine && evt.holder && !isMe(evt.holder, myDeviceId)) {
        takeover = { mesaId, holder: evt.holder };
      }
      break;
    case "revoked":
      if (evt.holder) next[mesaId] = normalizeLease(evt);
      else delete next[mesaId];
      if (
        (evt.previousHolder && isMe(evt.previousHolder, myDeviceId)) ||
        (mine && evt.leaseId && mine.leaseId === evt.leaseId)
      ) {
        takeover = { mesaId, holder: evt.holder || null };
      }
      break;
    case "released":
    case "expired":
      // Solo borramos si el evento es del lease vigente (un "released"
      // tardío de un lease viejo no debe borrar al holder actual).
      if (!next[mesaId] || !evt.leaseId || next[mesaId].leaseId === evt.leaseId) {
        delete next[mesaId];
      }
      // Mi lease caducó (móvil bloqueado/congelado > TTL sin heartbeat):
      // nadie me ha quitado la mesa → NO es un takeover. Se pide
      // re-adquirirla en silencio si sigue libre (mesaLeaseManager).
      if (action === "expired" && mine && (!evt.leaseId || mine.leaseId === evt.leaseId)) {
        reacquire = mesaId;
      }
      break;
    default:
      break;
  }
  return { byMesaId: next, takeover, reacquire };
}

/** Quita de la presencia los leases caducados (por si se perdió el evento). */
export function pruneExpired(byMesaId, now = Date.now()) {
  let changed = false;
  const next = {};
  Object.entries(byMesaId || {}).forEach(([k, l]) => {
    if (isLeaseExpired(l, now)) changed = true;
    else next[k] = l;
  });
  return changed ? next : byMesaId;
}

// ── Heartbeat ────────────────────────────────────────────────────────

/** leaseIds de todas mis mesas (para un único POST de heartbeat). */
export function heartbeatLeaseIds(myLeases) {
  return Object.values(myLeases || {})
    .map((l) => l?.leaseId)
    .filter(Boolean);
}

/** mesaIds de mis leases que el servidor da por perdidos. */
export function lostMesaIds(myLeases, result) {
  const lost = new Set((result?.lost || []).map(String));
  if (!lost.size) return [];
  return Object.entries(myLeases || {})
    .filter(([, l]) => l?.leaseId && lost.has(String(l.leaseId)))
    .map(([mesaId]) => mesaId);
}

// ── Header en mutaciones ─────────────────────────────────────────────

/** Añade `X-Mesa-Lease` a una config de axios si es mutación y hay lease. */
export function attachLeaseHeader(config, currentLease) {
  if (!config || !currentLease?.leaseId) return config;
  if (!MUTATING.has(String(config.method || "get").toLowerCase())) return config;
  // Los propios endpoints de lease (adquirir otra mesa, heartbeat, soltar)
  // no son mutaciones "dentro" de la mesa actual.
  if (/(^|\/)mesas\/(lease\/|[^/]+\/lease$)/i.test(String(config.url || ""))) return config;
  config.headers = config.headers || {};
  if (!config.headers[LEASE_HEADER]) config.headers[LEASE_HEADER] = currentLease.leaseId;
  return config;
}

// ── Errores de lease ─────────────────────────────────────────────────

/** Lee `{ code, holder }` de un error (body normalizado o axios crudo). */
export function leaseErrorInfo(err) {
  if (!err) return null;
  const body = err.response?.data && typeof err.response.data === "object" ? err.response.data : err;
  const code = body?.code || body?.errorCode || err?.errorCode || null;
  if (code !== "LEASE_HELD" && code !== "LEASE_CHARGING") return null;
  return { code, holder: body?.holder || null, message: body?.message || null };
}

// ── Último cobro compartido ──────────────────────────────────────────

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Evento `payment/{rid}` (o item de GET pedidos/pagos/last) → payload de
 * `setLastPayment`, con la misma forma que `buildLastPayment` + campos de
 * quién/dónde.
 */
export function lastPaymentFromEvent(evt, myDeviceId) {
  if (!evt) return null;
  const cash = num(evt.cash);
  const card = num(evt.card);
  const total = evt.total != null ? num(evt.total) : cash + card;
  const cashGiven = num(evt.cashGiven);
  return {
    paymentHistoryId: evt.paymentHistoryId ?? null,
    idSession: evt.idSession ?? null,
    idMesa: evt.idMesa ?? null,
    mesaName: evt.mesaName ?? null,
    zonaId: evt.zonaId ?? null,
    zonaName: evt.zonaName ?? null,
    total,
    cash,
    card,
    cashGiven,
    change: evt.changeGiven != null ? num(evt.changeGiven) : cashGiven > 0 ? Math.max(0, cashGiven - total) : 0,
    tipsEfectivo: num(evt.tipCash),
    tipsTarjeta: num(evt.tipCard),
    paymentMethodId: evt.paymentMethodId ?? null,
    paymentMethodName: evt.paymentMethodName ?? null,
    isPartial: !!evt.isPartial,
    remainingPending: evt.remainingPending != null ? num(evt.remainingPending) : null,
    employeeName: evt.employeeName ?? null,
    deviceLabel: evt.originDeviceLabel ?? null,
    originDeviceId: evt.originDeviceId ?? null,
    isMine: isMe(evt.originDeviceId, myDeviceId),
    reversed: !!evt.reversed,
    timestamp: toMs(evt.ts) ?? Date.now(),
  };
}

// Margen para reemplazar el "último cobro" optimista del propio dispositivo
// (sin paymentHistoryId) por el evento del servidor que llega después.
const OWN_OPTIMISTIC_WINDOW_MS = 15_000;

/**
 * ¿El cobro entrante debe sustituir al que se muestra?
 *  - mismo paymentHistoryId → solo si cambia algo (p.ej. anulado).
 *  - un anulado de otro cobro distinto al mostrado → no (no es el último).
 *  - si no, gana el más reciente; el optimista propio sin id se sustituye
 *    por su evento aunque el reloj del servidor vaya un poco por detrás.
 */
export function shouldReplaceLastPayment(current, incoming) {
  if (!incoming) return false;
  if (!current) return !incoming.reversed;
  if (
    incoming.paymentHistoryId != null &&
    current.paymentHistoryId != null &&
    String(incoming.paymentHistoryId) === String(current.paymentHistoryId)
  ) {
    return !!incoming.reversed !== !!current.reversed || incoming.total !== current.total;
  }
  if (incoming.reversed) return false;
  if (current.paymentHistoryId == null && incoming.isMine) {
    return incoming.timestamp >= (current.timestamp || 0) - OWN_OPTIMISTIC_WINDOW_MS;
  }
  return incoming.timestamp >= (current.timestamp || 0);
}

/** Aplica la actualización manteniendo `zonaId` del optimista si el evento no lo trae. */
export function mergeLastPayment(current, incoming) {
  if (!shouldReplaceLastPayment(current, incoming)) return current;
  if (!current) return incoming;
  return {
    ...incoming,
    zonaId: incoming.zonaId ?? current.zonaId ?? null,
  };
}

/**
 * Qué hacer cuando ESTE dispositivo pierde su lease sin que nadie se lo
 * haya quitado de forma explícita (heartbeat `lost`, `expired`, móvil
 * bloqueado más que el TTL):
 *  - "release":   ya no estoy dentro de esa mesa → soltar sin avisar.
 *  - "takeover":  otro dispositivo la tiene ahora → aviso + salir al plano
 *                 (las líneas sin enviar quedan en el borrador).
 *  - "reacquire": sigue libre → la vuelvo a pedir en silencio.
 */
export function decideLostLease({ inside, presenceHolder, myDeviceId }) {
  if (!inside) return "release";
  if (presenceHolder && !isMe(presenceHolder, myDeviceId)) return "takeover";
  return "reacquire";
}

// ── Rutas con lease (regla usuario 2026-10-07) ─────────────────────
// El lease, su heartbeat y la presencia solo viven en el TPV: plano de mesas,
// carta/producto/cobro del camarero y pago. Cualquier otra sección
// (Configuración, Estadísticas, Inventario, Empleados, Reservas, Admin…) lo
// suelta y no pide nada.
export const LEASE_ROUTE_PREFIXES = [
  "/app/tucarta/tpv",
  "/app/tpv/camarero",
  "/app/tpv/pago",
  "/app/tpv/producto",
];

export function isLeaseRoute(pathname) {
  const p = String(pathname || "").toLowerCase();
  return LEASE_ROUTE_PREFIXES.some((x) => p === x || p.startsWith(`${x}/`));
}
